import { PLANTS, PLANT_TYPE } from '../data/plants.js';
import { PlantTile } from './PlantTile.jsx';

function plantTotalCost(plant, dynamicOpex, dynamicCapital, remainingYears) {
  const effectiveOpex = dynamicOpex?.final ?? plant.opex;
  const effectiveCapital = dynamicCapital?.effectiveCapital ?? plant.capital;
  return effectiveCapital + effectiveOpex * remainingYears;
}

function adjustedAverageOutput(plant, outputMultiplierMap) {
  return plant.averageOutput * (outputMultiplierMap?.[plant.id] ?? 1);
}

function sortableFirmCapacity(plant, dynamicFirmMap) {
  if (plant.type === 'storage') {
    return (plant.capacityUnits ?? 0) * (plant.firmCapacityCredit ?? 0);
  }
  return (
    dynamicFirmMap?.[plant.id]?.effective ??
    (plant.capacityUnits ?? 0) * (plant.firmCapacityCredit ?? 0)
  );
}

function sortPlants(
  plants,
  sortMode,
  dynamicOpexMap,
  dynamicCapitalMap,
  dynamicFirmMap,
  remainingYears,
  outputMultiplierMap
) {
  const sorted = [...plants];
  switch (sortMode) {
    case 'output':
      sorted.sort(
        (a, b) =>
          adjustedAverageOutput(a, outputMultiplierMap) -
          adjustedAverageOutput(b, outputMultiplierMap)
      );
      break;
    case 'cost':
      sorted.sort(
        (a, b) =>
          plantTotalCost(
            a,
            dynamicOpexMap?.[a.id],
            dynamicCapitalMap?.[a.id],
            remainingYears
          ) -
          plantTotalCost(
            b,
            dynamicOpexMap?.[b.id],
            dynamicCapitalMap?.[b.id],
            remainingYears
          )
      );
      break;
    case 'emissions':
      sorted.sort((a, b) => a.emissions - b.emissions);
      break;
    case 'firm':
      sorted.sort(
        (a, b) =>
          sortableFirmCapacity(a, dynamicFirmMap) -
          sortableFirmCapacity(b, dynamicFirmMap)
      );
      break;
    case 'efficiency':
      sorted.sort((a, b) => {
        const effA =
          (adjustedAverageOutput(a, outputMultiplierMap) * remainingYears) /
          plantTotalCost(
            a,
            dynamicOpexMap?.[a.id],
            dynamicCapitalMap?.[a.id],
            remainingYears
          );
        const effB =
          (adjustedAverageOutput(b, outputMultiplierMap) * remainingYears) /
          plantTotalCost(
            b,
            dynamicOpexMap?.[b.id],
            dynamicCapitalMap?.[b.id],
            remainingYears
          );
        return effB - effA;
      });
      break;
    case 'type':
      sorted.sort((a, b) => {
        if (a.family !== b.family) return a.family.localeCompare(b.family);
        if (a.type !== b.type) {
          return a.type === PLANT_TYPE.RELIABLE ? -1 : 1;
        }
        return a.capital - b.capital;
      });
      break;
    default:
      sorted.sort((a, b) => a.capital - b.capital);
      break;
  }
  return sorted;
}

export function PlantPicker({
  plants = PLANTS,
  slotsRemaining,
  sortMode,
  remainingYears = 1,
  buildLocked = false,
  constructingPlantId,
  constructingDurationMs,
  dynamicOpexMap,
  dynamicCapitalMap,
  dynamicFirmMap,
  outputMultiplierMap,
  showFirmCapacity = false,
  onSelect
}) {
  const disabled = slotsRemaining <= 0 || buildLocked;
  const visible = sortPlants(
    plants,
    sortMode,
    dynamicOpexMap,
    dynamicCapitalMap,
    dynamicFirmMap,
    remainingYears,
    outputMultiplierMap
  );

  return (
    <div className="plant-picker">
      {buildLocked ? (
        <p className="plant-picker-instruction">
          Construction in progress. Controls will unlock when the build is
          complete.
        </p>
      ) : slotsRemaining <= 0 ? (
        <p className="plant-picker-instruction">
          All slots filled. Remove a selection to change your plan.
        </p>
      ) : null}
      <ul className="plant-picker-grid" role="list">
        {visible.map((plant) => (
          <li key={plant.id}>
            <PlantTile
              plant={plant}
              selectable
              disabled={disabled}
              constructing={plant.id === constructingPlantId}
              constructionDurationMs={constructingDurationMs}
              dynamicOpex={dynamicOpexMap?.[plant.id]}
              dynamicCapital={dynamicCapitalMap?.[plant.id]}
              dynamicFirm={dynamicFirmMap?.[plant.id]}
              outputMultiplier={outputMultiplierMap?.[plant.id] ?? 1}
              showFirmCapacity={showFirmCapacity}
              onSelect={() => onSelect(plant.id)}
              testId={`plant-pick-${plant.id}`}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
